import { useEffect, useMemo, useRef } from "react";
import {
  MULTI_ACCOUNT_INBOX_PREFETCH_DEFER_MS,
  INBOX_BOOTSTRAP_FOLDER_GAP_MS,
} from "@/lib/constants/inbox";
import { isDraftSaveInFlight } from "@/lib/compose/draftSaveLock";
import {
  getFolderSnapshot,
  isFolderSnapshotWarm,
} from "@/lib/mail/inboxFolderBus";
import { idleDelay, syncFolderFromServerCache } from "@/lib/mail/folderWarmup";
import { isImapClientBusy } from "@/lib/mail/imapClientLock";
import type { MailAccountSummary } from "@/lib/types/mail";

/**
 * Au boot : lit DD02 INBOX pour chaque compte (sans IMAP), séquentiellement.
 * Attend la visibilité + délai pour laisser hydrater le compte actif d’abord.
 */
export function useMultAccountInboxPrefetch(
  accounts: MailAccountSummary[],
  options?: { enabled?: boolean }
): void {
  const enabled = options?.enabled !== false;
  const prefetchedRef = useRef(new Set<number>());
  const accountIds = useMemo(
    () => accounts.map((a) => a.id).sort((a, b) => a - b).join(","),
    [accounts]
  );

  useEffect(() => {
    if (!enabled || accounts.length === 0) return;

    let cancelled = false;

    const runPrefetch = async () => {
      for (const account of accounts) {
        if (cancelled) break;
        if (document.visibilityState !== "visible") break;

        if (prefetchedRef.current.has(account.id)) continue;

        const snap = getFolderSnapshot(account.id, "INBOX");
        if (isFolderSnapshotWarm(snap)) {
          prefetchedRef.current.add(account.id);
          continue;
        }

        while (!cancelled && (isDraftSaveInFlight() || isImapClientBusy())) {
          await idleDelay(400);
        }
        if (cancelled || document.visibilityState !== "visible") break;

        await syncFolderFromServerCache(account.id, "INBOX");

        prefetchedRef.current.add(account.id);

        if (cancelled) break;
        await idleDelay(INBOX_BOOTSTRAP_FOLDER_GAP_MS);
      }
    };

    let timer: number | null = null;
    const schedule = () => {
      if (timer != null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        void runPrefetch();
      }, MULTI_ACCOUNT_INBOX_PREFETCH_DEFER_MS);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        schedule();
      }
    };

    if (document.visibilityState === "visible") {
      schedule();
    }
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [accountIds, accounts, enabled]);
}
